import { type ID, type ISODateTime, type Nullable } from '@/model/Common.model.ts';
import { useContext, useRef, useEffect } from 'react';
import {
  WorkflowContext,
  type ChangeAnimalCommandType,
  type ChangeCageCommandType,
  type SearchQueryCommandType,
  type UpdateAlertsCommandType,
  type UpdateMeasurementsCommandType,
  type MeasurementsError,
} from '@/utils/workflow/WorkflowProvider.tsx';
import { type AlertState, type CageAlertState, type WorkflowSetup } from '@/components/Workflow/Show/Workflow.model.ts';
import { type Animal } from '@/model/Animal.model.ts';
import { type Measurement } from '@/model/Measurement.model.ts';
import { _notNil } from '@/littledash.ts';
import type { PercentageTypes } from '@/components/Subjects/Show/Measurements/Form/Form.utils.ts';

interface UseSelectedAnimalResponse {
  selectedAnimal: ID | undefined;
}

interface UseSelectedCageResponse {
  selectedCage: ID | 'deceased' | undefined;
}

interface UseChangeAnimalResponse {
  changeAnimal: (payload: ChangeAnimalCommandType) => void;
  changeCage: (payload: ChangeCageCommandType) => void;
}

interface UseWorkflowSetupResponse {
  workflowSetup: WorkflowSetup | undefined;
  updateWorkflowSetup: (workflowSetup: WorkflowSetup) => void;
}

interface UseSubjectsResponse {
  availableSubjects: Array<Animal> | undefined;
  originalSubjects: Array<Animal> | undefined;
  filteredCageIds: Array<ID> | undefined;
}

interface UseUpdateSubjectResponse {
  updateOriginalSubjects: (originalSubjects: Array<Animal>) => void;
  updateSingleSubject: (updatedSubject: Animal) => void;
}

interface UseSearchQueryResponse {
  handleSearchQuery: (payload: SearchQueryCommandType) => void;
  searchQuery: string | undefined;
}

interface UseAlertsResponse {
  animalAlerts: AlertState | undefined;
  cageAlerts: CageAlertState | undefined;
  updateAlerts: (payload: UpdateAlertsCommandType) => void;
}

interface UseMeasurementsResponse {
  animalMeasurements: Array<Measurement> | undefined;
  todaysMeasurement: Nullable<Measurement>;
  measurementsLoading: boolean;
  measurementsError: Nullable<MeasurementsError>;
}

interface UseUpdateMeasurementsResponse {
  updateMeasurements: (payload: UpdateMeasurementsCommandType) => void;
}

export const useSelectedAnimal = (): UseSelectedAnimalResponse => {
  const { selectedAnimal } = useContext(WorkflowContext);
  return { selectedAnimal };
};

interface UseSavedValueDifferenceResponse {
  savedValueDifference: Record<ID, Record<string, PercentageTypes>> | undefined;
  updateSavedValueDifference: (animalId: ID, valueDifference: Record<string, PercentageTypes>) => void;
  clearSavedValueDifference: (animalId: ID) => void;
  clearAllSavedValueDifference: () => void;
}

export const useSavedValueDifference = (): UseSavedValueDifferenceResponse => {
  const { savedValueDifference, updateSavedValueDifference, clearSavedValueDifference, clearAllSavedValueDifference } =
    useContext(WorkflowContext);
  return { savedValueDifference, updateSavedValueDifference, clearSavedValueDifference, clearAllSavedValueDifference };
};

export const useSelectedCage = (): UseSelectedCageResponse => {
  const { selectedCage } = useContext(WorkflowContext);
  return { selectedCage };
};

export const useChangeAnimalActions = (): UseChangeAnimalResponse => {
  const { changeAnimal, changeCage } = useContext(WorkflowContext);
  return { changeAnimal, changeCage };
};

export const useWorkflowSetup = (): UseWorkflowSetupResponse => {
  const { workflowSetup, updateWorkflowSetup } = useContext(WorkflowContext);
  return { workflowSetup, updateWorkflowSetup };
};

export const useSubjects = (): UseSubjectsResponse => {
  const { originalSubjects, filteredSubjects, filteredCageIds } = useContext(WorkflowContext);
  const availableSubjects = filteredSubjects ?? originalSubjects;
  return { availableSubjects, originalSubjects, filteredCageIds };
};

export const useUpdateSubjectActions = (): UseUpdateSubjectResponse => {
  const { updateOriginalSubjects, updateSingleSubject } = useContext(WorkflowContext);
  return { updateOriginalSubjects, updateSingleSubject };
};

export const useSearchQuery = (): UseSearchQueryResponse => {
  const { handleSearchQuery, searchQuery } = useContext(WorkflowContext);
  return { handleSearchQuery, searchQuery };
};

export const useAlerts = (): UseAlertsResponse => {
  const { animalAlerts, cageAlerts, updateAlerts } = useContext(WorkflowContext);
  return { animalAlerts, cageAlerts, updateAlerts };
};

export const useMeasurements = (animalId: ID | undefined): UseMeasurementsResponse => {
  const { animalMeasurements, todaysMeasurements, measurementsLoading, measurementsErrors } =
    useContext(WorkflowContext);

  return {
    animalMeasurements: _notNil(animalId) ? animalMeasurements?.[animalId] : undefined,
    todaysMeasurement: _notNil(animalId) ? todaysMeasurements?.[animalId] : undefined,
    measurementsLoading: _notNil(animalId) ? (measurementsLoading?.[animalId] ?? false) : false,
    measurementsError: _notNil(animalId) ? measurementsErrors?.[animalId] : undefined,
  };
};

export const useUpdateMeasurements = (): UseUpdateMeasurementsResponse => {
  const { updateMeasurements } = useContext(WorkflowContext);
  return { updateMeasurements };
};

interface UseMeasuredAtInfoResponse {
  measured_at: ISODateTime | undefined;
  hasMeasuredAtChanged: boolean;
}

export const useMeasuredAtInfo = (): UseMeasuredAtInfoResponse => {
  const { workflowSetup } = useWorkflowSetup();
  const prevMeasuredAtRef = useRef(workflowSetup?.measured_at);

  useEffect(() => {
    prevMeasuredAtRef.current = workflowSetup?.measured_at;
  }, [workflowSetup?.measured_at]);

  return {
    measured_at: workflowSetup?.measured_at,
    // eslint-disable-next-line react-hooks/refs
    hasMeasuredAtChanged: prevMeasuredAtRef.current !== workflowSetup?.measured_at,
  };
};
